import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { hashtagAPI } from '../../utils/api';

// Async thunk для получения хештегов
export const fetchHashtags = createAsyncThunk(
  'hashtags/fetchHashtags',
  async ({ limit = 20, offset = 0, search = '' } = {}, { rejectWithValue }) => {
    try {
      const response = await hashtagAPI.getHashtags({ limit, offset, search });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch hashtags');
    }
  }
);

// Async thunk для получения трендовых хештегов
export const fetchTrendingHashtags = createAsyncThunk(
  'hashtags/fetchTrendingHashtags',
  async ({ limit = 10, period = 'week' } = {}, { rejectWithValue }) => {
    try {
      const response = await hashtagAPI.getTrendingHashtags({ limit, period });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch trending hashtags');
    }
  }
);

// Async thunk для создания хештега
export const createHashtag = createAsyncThunk(
  'hashtags/createHashtag',
  async (hashtagData, { rejectWithValue }) => {
    try {
      const response = await hashtagAPI.createHashtag(hashtagData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to create hashtag');
    }
  }
);

// Async thunk для добавления хештегов к контенту
export const addHashtagsToContent = createAsyncThunk(
  'hashtags/addHashtagsToContent',
  async ({ contentType, contentId, hashtags }, { rejectWithValue }) => {
    try {
      const response = await hashtagAPI.addHashtagToContent(contentType, contentId, hashtags);
      return {
        contentType,
        contentId,
        hashtags: response.data?.hashtags ?? response.data ?? [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to add hashtags to content');
    }
  }
);

// Async thunk для удаления хештегов из контента (бэкенд принимает один id или имя за запрос)
export const removeHashtagsFromContent = createAsyncThunk(
  'hashtags/removeHashtagsFromContent',
  async ({ contentType, contentId, hashtags }, { rejectWithValue }) => {
    try {
      const list = Array.isArray(hashtags) ? hashtags : [hashtags];
      let lastResponse = null;
      for (const idOrName of list) {
        lastResponse = await hashtagAPI.removeHashtagFromContent(contentType, contentId, idOrName);
      }
      return {
        contentType,
        contentId,
        hashtags: lastResponse?.data?.hashtags ?? lastResponse?.data ?? [],
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove hashtags from content');
    }
  }
);

const hashtagSlice = createSlice({
  name: 'hashtags',
  initialState: {
    hashtags: [],
    trendingHashtags: [],
    contentHashtags: {}, // { 'contentType_contentId': [hashtags] }
    isLoading: false,
    error: null,
    hasMore: true,
    searchQuery: '',
  },
  reducers: {
    // Обновление поискового запроса
    updateSearchQuery: (state, action) => {
      state.searchQuery = action.payload;
    },

    // Добавление хештегов к контенту (оптимистичное обновление)
    addHashtagsOptimistic: (state, action) => {
      const { contentType, contentId, hashtags } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      if (!state.contentHashtags[key]) {
        state.contentHashtags[key] = [];
      }
      
      // Добавляем новые хештеги, избегая дублирования
      hashtags.forEach(hashtag => {
        const exists = state.contentHashtags[key].find(h => h.id === hashtag.id);
        if (!exists) {
          state.contentHashtags[key].push(hashtag);
        }
      });
    },

    // Удаление хештегов из контента (оптимистичное обновление)
    removeHashtagsOptimistic: (state, action) => {
      const { contentType, contentId, hashtagIds } = action.payload;
      const key = `${contentType}_${contentId}`;
      
      if (state.contentHashtags[key]) {
        state.contentHashtags[key] = state.contentHashtags[key].filter(
          hashtag => !hashtagIds.includes(hashtag.id)
        );
      }
    },

    // Очистка ошибки
    clearError: (state) => {
      state.error = null;
    },

    // Сброс состояния
    resetHashtagState: (state) => {
      state.hashtags = [];
      state.trendingHashtags = [];
      state.contentHashtags = {};
      state.isLoading = false;
      state.error = null;
      state.hasMore = true;
      state.searchQuery = '';
    },
  },
  extraReducers: (builder) => {
    builder
      // Получение хештегов
      .addCase(fetchHashtags.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHashtags.fulfilled, (state, action) => {
        state.isLoading = false;
        const { hashtags, hasMore } = action.payload;
        
        // Если это поиск, заменяем список
        if (state.searchQuery) {
          state.hashtags = hashtags;
        } else {
          // Иначе добавляем к существующему списку
          const existingIds = new Set(state.hashtags.map(h => h.id));
          const newHashtags = hashtags.filter(h => !existingIds.has(h.id));
          state.hashtags = [...state.hashtags, ...newHashtags];
        }
        
        state.hasMore = hasMore;
      })
      .addCase(fetchHashtags.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Получение трендовых хештегов
      .addCase(fetchTrendingHashtags.fulfilled, (state, action) => {
        state.trendingHashtags = action.payload;
      })
      .addCase(fetchTrendingHashtags.rejected, (state, action) => {
        console.error('Failed to fetch trending hashtags:', action.payload);
      })
      
      // Создание хештега
      .addCase(createHashtag.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createHashtag.fulfilled, (state, action) => {
        state.isLoading = false;
        const hashtag = action.payload;
        
        // Добавляем в список, если его там нет
        const exists = state.hashtags.find(h => h.id === hashtag.id);
        if (!exists) {
          state.hashtags.unshift(hashtag);
        }
      })
      .addCase(createHashtag.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      
      // Добавление хештегов к контенту
      .addCase(addHashtagsToContent.fulfilled, (state, action) => {
        const { contentType, contentId, hashtags } = action.payload;
        const key = `${contentType}_${contentId}`;
        state.contentHashtags[key] = hashtags;
      })
      .addCase(addHashtagsToContent.rejected, (state, action) => {
        console.error('Failed to add hashtags to content:', action.payload);
      })
      
      // Удаление хештегов из контента
      .addCase(removeHashtagsFromContent.fulfilled, (state, action) => {
        const { contentType, contentId, hashtags } = action.payload;
        const key = `${contentType}_${contentId}`;
        state.contentHashtags[key] = hashtags;
      })
      .addCase(removeHashtagsFromContent.rejected, (state, action) => {
        console.error('Failed to remove hashtags from content:', action.payload);
      });
  },
});

// Экспортируем actions
export const {
  updateSearchQuery,
  addHashtagsOptimistic,
  removeHashtagsOptimistic,
  clearError,
  resetHashtagState,
} = hashtagSlice.actions;

// Селекторы
export const selectHashtags = (state) => state.hashtags.hashtags;
export const selectTrendingHashtags = (state) => state.hashtags.trendingHashtags;
export const selectContentHashtags = (state, contentType, contentId) => {
  const key = `${contentType}_${contentId}`;
  return state.hashtags.contentHashtags[key] || [];
};
export const selectHashtagLoading = (state) => state.hashtags.isLoading;
export const selectHashtagError = (state) => state.hashtags.error;
export const selectHashtagSearchQuery = (state) => state.hashtags.searchQuery;
export const selectHasMoreHashtags = (state) => state.hashtags.hasMore;

export default hashtagSlice.reducer;
